/**
 * Platform Revenue Dashboard - Comprehensive monetization overview
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  PlatformEarningsSummary,
  RevenueEventHistory,
  RevenueSplitExplanation
} from '@/components/revenue';
import { RefreshCw, Download, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function RevenueManagement() {
  const { session } = useAuth();
  const [loading, setLoading] = useState(true);
  const [platformEarnings, setPlatformEarnings] = useState(null);
  const [events, setEvents] = useState([]);
  const [splitInfo, setSplitInfo] = useState(null);
  const [period, setPeriod] = useState(30);
  const [page, setPage] = useState(1);
  
  const fetchData = async () => {
    if (!session?.access_token) return;
    
    setLoading(true);
    try {
      // Fetch platform earnings
      const earningsRes = await fetch(`${API_URL}/api/revenue/platform/earnings?days=${period}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      if (earningsRes.ok) {
        setPlatformEarnings(await earningsRes.json());
      }
      
      // Fetch revenue events
      const eventsRes = await fetch(`${API_URL}/api/revenue/platform/events?days=${period}&page=${page}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      if (eventsRes.ok) {
        const data = await eventsRes.json();
        setEvents(data.events || []);
      }
      
      // Fetch split info (public endpoint)
      const splitRes = await fetch(`${API_URL}/api/revenue/split-info`);
      if (splitRes.ok) {
        setSplitInfo(await splitRes.json());
      }
    } catch (error) {
      console.error('Failed to fetch revenue data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [session?.access_token, period, page]);
  
  const loadMoreEvents = () => {
    setPage(p => p + 1);
  };
  
  return (
    <div className="p-6 space-y-6" data-testid="revenue-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Revenue Management</h1>
          <p className="text-white/50 text-sm mt-1">Platform monetization and revenue splits</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Period Selector */}
          <div className="flex items-center gap-2 bg-white/[0.04] rounded-lg p-1">
            {[7, 30, 90].map(days => (
              <button
                key={days}
                onClick={() => { setPeriod(days); setPage(1); }}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                  period === days 
                    ? 'bg-white/10 text-white' 
                    : 'text-white/50 hover:text-white'
                }`}
              >
                {days}d
              </button>
            ))}
          </div>
          
          <Button 
            variant="ghost" 
            size="icon"
            onClick={fetchData}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
      
      {loading && !platformEarnings ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 text-white/20 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Platform earnings */}
            <PlatformEarningsSummary data={platformEarnings} />
            
            {/* Revenue events */}
            <RevenueEventHistory 
              events={events} 
              onLoadMore={events.length >= 50 ? loadMoreEvents : null}
            />
          </div>
          
          {/* Sidebar - 1 column */}
          <div className="space-y-6">
            {/* Split explanation */}
            <RevenueSplitExplanation data={splitInfo} />
          </div>
        </div>
      )}
    </div>
  );
}
